<template>
    <div class="reports-page">
        <div class="reports-toolbar">
            <div class="reports-toolbar__top">
                <div class="reports-toolbar__heading">
                    <h5 class="mb-0">Отчеты</h5>
                    <b-badge variant="secondary">Всего: {{ total }}</b-badge>
                    <b-badge variant="success">Действующие: {{ counts.active }}</b-badge>
                    <b-badge variant="danger">Аннулированные: {{ counts.annulled }}</b-badge>
                    <b-badge v-if="counts.problem" variant="danger">Проблемные: {{ counts.problem }}</b-badge>
                </div>
                <div class="reports-toolbar__meta">
                    <b-button
                        size="sm"
                        variant="primary"
                        class="reports-submit-button"
                        to="/shift-report-master"
                    >
                        <i class="fas fa-file-signature"></i>
                        Сдать отчёт по смене
                    </b-button>
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        class="reports-mobile-filter-toggle d-md-none"
                        @click="filtersVisible = !filtersVisible"
                    >
                        <i class="fas fa-filter"></i>
                        Фильтры
                        <b-badge v-if="activeFilterChips.length" variant="primary">
                            {{ activeFilterChips.length }}
                        </b-badge>
                    </b-button>
                    <b-button
                        v-if="canRequestAnnulmentForVisibleReports"
                        size="sm"
                        :variant="deleteMode ? 'danger' : 'outline-danger'"
                        class="reports-delete-mode-button"
                        @click="toggleDeleteMode"
                    >
                        <i class="fas fa-ban"></i>
                        Аннулирование отчёта
                    </b-button>
                    <b-button
                        v-if="canManageReportsAccess"
                        size="sm"
                        variant="outline-primary"
                        class="reports-access-button"
                        @click="openAccessSettings"
                    >
                        <i class="fas fa-user-shield"></i>
                        Настройки
                    </b-button>
                </div>
            </div>

            <div class="reports-report-status-filter" role="group" aria-label="Статус отчёта">
                <span>Статус отчёта</span>
                <button
                    v-for="option in reportStatusOptions"
                    :key="option.value"
                    type="button"
                    :class="{'reports-report-status-filter__button--active': reportStatusModel === option.value}"
                    class="reports-report-status-filter__button"
                    @click="reportStatusModel = option.value"
                >
                    {{ option.text }}
                    <b-badge :variant="option.variant">{{ option.count }}</b-badge>
                </button>
            </div>

            <div class="reports-filters-shell" :class="{'reports-filters-shell--open': filtersVisible}">
                <b-row class="reports-filters" no-gutters>
                    <b-col cols="12" lg="2" class="pr-lg-2 mb-2">
                        <div class="reports-status-switch" role="group" aria-label="Режим объектов">
                            <button
                                type="button"
                                class="reports-status-switch__button"
                                :class="{'reports-status-switch__button--active': objectStatusModel === 'active'}"
                                @click="setObjectStatus('active')"
                            >
                                Действующие
                            </button>
                            <button
                                type="button"
                                class="reports-status-switch__button"
                                :class="{'reports-status-switch__button--active': objectStatusModel === 'archive'}"
                                @click="setObjectStatus('archive')"
                            >
                                Архив
                            </button>
                        </div>
                    </b-col>
                    <b-col cols="12" lg="2" class="pr-lg-2 mb-2">
                        <b-form-input
                            v-model="queryModel"
                            size="sm"
                            placeholder="Поиск по номеру, объекту, автору, причинам"
                            @keyup.enter="reload"
                        />
                    </b-col>
                    <b-col cols="6" lg="1" class="pr-1 pr-lg-2 mb-2">
                        <v-select
                            v-model="issueModel"
                            :options="issueOptions"
                            label="text"
                            :reduce="option => option.value"
                            :clearable="false"
                            :searchable="true"
                            class="reports-filter-select"
                            append-to-body
                        />
                    </b-col>
                    <b-col cols="6" lg="2" class="pl-1 pr-lg-2 mb-2">
                        <v-select
                            v-model="buildingProcessModel"
                            :options="buildingProcessOptions"
                            label="text"
                            :reduce="option => option.value"
                            :clearable="false"
                            :searchable="true"
                            class="reports-filter-select"
                            append-to-body
                        />
                    </b-col>
                    <b-col cols="6" lg="2" class="pr-1 pr-lg-2 mb-2">
                        <v-select
                            v-model="authorModel"
                            :options="authorOptions"
                            label="text"
                            :reduce="option => option.value"
                            :clearable="false"
                            :searchable="true"
                            class="reports-filter-select"
                            append-to-body
                        />
                    </b-col>
                    <b-col cols="6" lg="1" class="pl-1 pr-lg-2 mb-2">
                        <b-form-input v-model="dateFromModel" type="date" size="sm" aria-label="Дата отчёта с" />
                    </b-col>
                    <b-col cols="6" lg="1" class="pr-1 pr-lg-2 mb-2">
                        <b-form-input v-model="dateToModel" type="date" size="sm" aria-label="Дата отчёта по" />
                    </b-col>
                    <b-col cols="6" lg="1" class="pl-1 mb-2">
                        <b-button block size="sm" variant="outline-secondary" @click="resetFilters">
                            <i class="fas fa-times"></i>
                            <span class="d-none d-md-inline ml-1">Сбросить</span>
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <div v-if="activeFilterChips.length" class="reports-active-filters">
                <span class="reports-active-filters__label">
                    <i class="fas fa-filter"></i>
                    Включены фильтры:
                </span>
                <button
                    v-for="chip in activeFilterChips"
                    :key="chip.key"
                    type="button"
                    class="reports-active-filter"
                    :class="`reports-active-filter--${chip.variant}`"
                    :title="`Снять фильтр: ${chip.text}`"
                    @click="clearFilterChip(chip.key)"
                >
                    <i class="fas fa-check" aria-hidden="true"></i>
                    <span class="reports-active-filter__text">{{ chip.text }}</span>
                    <i class="fas fa-times reports-active-filter__remove" aria-hidden="true"></i>
                </button>
                <button
                    type="button"
                    class="reports-active-filters__reset"
                    @click="resetFilters"
                >
                    Сбросить все
                </button>
            </div>

            <div v-if="deleteMode" class="reports-delete-bar">
                <div class="reports-delete-bar__info">
                    <strong>Режим аннулирования</strong>
                    <span>Выберите один или несколько действующих отчётов одного объекта</span>
                    <b-badge variant="danger">Выбрано: {{ selectedReportsForDelete.length }}</b-badge>
                </div>
                <div class="reports-delete-bar__actions">
                    <b-button
                        size="sm"
                        variant="danger"
                        :disabled="!selectedReportsForDelete.length"
                        @click="openDeleteRequestModal"
                    >
                        <i class="fas fa-file-signature"></i>
                        Создать заявку
                    </b-button>
                    <b-button size="sm" variant="outline-secondary" @click="cancelDeleteMode">
                        Отмена
                    </b-button>
                </div>
            </div>
        </div>

        <div class="reports-list">
            <div v-if="loading" class="reports-loading">
                <b-spinner small />
                <span>Загрузка отчетов...</span>
            </div>

            <div class="d-none d-md-block">
                <b-table
                    :items="items"
                    :fields="tableFields"
                    :busy="loading"
                    small
                    hover
                    responsive
                    sticky-header="calc(100vh - 220px)"
                    @row-clicked="openReport"
                    @sort-changed="onSortChanged"
                    :sort-by.sync="sortByModel"
                    :sort-desc.sync="sortDescModel"
                    :tbody-tr-class="reportRowClass"
                    class="reports-table"
                >
                    <template #cell(delete_select)="row">
                        <b-form-checkbox
                            :checked="isReportSelected(row.item)"
                            :disabled="!isReportSelected(row.item) && !canSelectReportForDelete(row.item)"
                            aria-label="Выбрать отчёт для аннулирования"
                            @click.native.stop
                            @change="checked => setReportDeleteSelected(row.item, checked)"
                        />
                    </template>

                    <template #cell(shift_id)="row">
                        <span :id="`report_row_${row.item.shift_id}`" class="reports-row-marker"></span>
                        <strong>№{{ row.item.shift_id }}</strong>
                        <i
                            v-if="row.item.telegram_message_id"
                            class="fab fa-telegram-plane text-info ml-1"
                            v-b-tooltip.hover
                            title="Telegram-сообщение зафиксировано"
                        ></i>
                    </template>

                    <template #cell(date_time_begin)="row">
                        <div class="reports-shift-date">{{ formatDate(row.item.date_time_begin) }}</div>
                        <div class="reports-shift-date">{{ formatTimeRange(row.item) }}</div>
                        <small
                            v-if="row.item.date_time_send"
                            class="d-block"
                            :class="row.item.flags && row.item.flags.late ? 'text-danger font-weight-bold' : 'text-muted'"
                        >
                            подан {{ formatDate(row.item.date_time_send) }}
                        </small>
                    </template>

                    <template #cell(object)="row">
                        <div class="font-weight-bold reports-object-name">
                            {{ row.item.object && row.item.object.name ? row.item.object.name : '-' }}
                        </div>
                    </template>

                    <template #cell(author)="row">
                        <div @click.stop>
                            <TelegramUserLink :user="row.item.author" fallback="-" name-order="last-first" />
                        </div>
                        <a
                            v-if="row.item.author.phone"
                            :href="phoneHref(row.item.author.phone)"
                            class="text-muted small"
                            @click.stop
                        >{{ formatPhone(row.item.author.phone) }}</a>
                    </template>

                    <template #cell(quantity_of_workers)="row">
                        <div class="reports-count reports-workers-count">
                            <i class="fas fa-hard-hat text-muted"></i>
                            {{ formatNumber(row.item.quantity_of_workers) }}
                        </div>
                    </template>

                    <template #cell(media)="row">
                        <div
                            :id="`report_media_preview_${row.item.shift_id}`"
                            class="reports-count reports-media-trigger"
                            tabindex="0"
                            @mouseenter="loadMediaPreview(row.item)"
                            @focusin="loadMediaPreview(row.item)"
                            @click.stop
                        >
                            <i class="fas fa-photo-video text-muted"></i>
                            {{ row.item.stats.media_count }}
                        </div>
                        <div class="reports-media-statuses">
                            <span
                                v-for="item in mediaDeliveryBadges(row.item)"
                                :key="item.key"
                                class="reports-media-status"
                                :class="`reports-media-status--${item.variant}`"
                            >
                                {{ item.text }}
                            </span>
                        </div>
                        <div
                            v-if="canManageReportPhotos(row.item)"
                            class="reports-media-actions"
                            @click.stop
                        >
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                class="reports-media-action-button"
                                @click="openPhotoUpload(row.item)"
                            >
                                <i class="fas fa-cloud-upload-alt"></i>
                                Догрузить фото
                            </b-button>
                            <b-button
                                v-if="canResendReportPhotos(row.item)"
                                size="sm"
                                variant="outline-warning"
                                class="reports-media-action-button"
                                @click="resendPhotos(row.item)"
                            >
                                <i class="fas fa-paper-plane"></i>
                                Отправить неотправленные
                            </b-button>
                        </div>
                        <b-popover
                            :target="`report_media_preview_${row.item.shift_id}`"
                            triggers="hover focus"
                            placement="left"
                            custom-class="reports-media-popover"
                        >
                            <template #title>
                                Фото отчёта №{{ row.item.shift_id }}
                            </template>
                            <div v-if="isMediaPreviewLoading(row.item)" class="reports-media-preview-loading">
                                <b-spinner small />
                                <span>Загрузка...</span>
                            </div>
                            <div v-else-if="mediaPreviewItems(row.item).length" class="reports-media-preview-grid">
                                <button
                                    v-for="(file, index) in mediaPreviewItems(row.item)"
                                    :key="file.id || index"
                                    type="button"
                                    class="reports-media-preview-tile"
                                    @click.stop="openMediaPreview(row.item, index)"
                                >
                                    <img
                                        v-if="isMediaImage(file)"
                                        :src="mediaThumbnailUrl(file)"
                                        :alt="file.name || file.filename || 'Фото отчёта'"
                                    >
                                    <span v-else>
                                        <i :class="isMediaVideo(file) ? 'fas fa-file-video' : 'fas fa-file'"></i>
                                    </span>
                                </button>
                                <small v-if="mediaPreviewTotal(row.item) > mediaPreviewItems(row.item).length" class="reports-media-preview-more">
                                    ещё {{ mediaPreviewTotal(row.item) - mediaPreviewItems(row.item).length }}
                                </small>
                            </div>
                            <div v-else class="text-muted small">
                                Фото по отчёту не найдены
                            </div>
                        </b-popover>
                    </template>

                    <template #cell(fuel)="row">
                        <div>{{ formatNumber(row.item.stats.fuel_total_value) }}</div>
                        <small class="text-muted">{{ row.item.stats.fuel_records_count }} зап.</small>
                    </template>

                    <template #cell(issues)="row">
                        <ReportBadges :report="row.item" compact show-empty />
                    </template>

                    <template #cell(comments)="row">
                        <div class="reports-count">
                            <i class="fas fa-comments text-muted"></i>
                            {{ row.item.stats.comments_count }}
                        </div>
                    </template>

                    <template #cell(actions)="row">
                        <div class="reports-row-actions" @click.stop>
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                class="reports-details-button"
                                title="Подробнее"
                                @click="openReportDetails(row.item)"
                            >
                                <i class="fas fa-external-link-alt" aria-hidden="true"></i>
                                <span>Подробнее</span>
                            </b-button>
                            <ReportActionsMenu
                                :report="row.item"
                                :show-photo-actions="false"
                                @upload-photos="openPhotoUpload"
                                @resend-photos="resendPhotos"
                                @resend-report="resendTelegramReport"
                                @delete-request="openSingleDeleteRequest"
                                @copy-link="copyReportLink"
                            />
                        </div>
                    </template>
                </b-table>
            </div>

            <div class="d-md-none reports-mobile-list">
                <article
                    v-for="report in items"
                    :key="report.shift_id"
                    role="button"
                    tabindex="0"
                    class="reports-mobile-card"
                    :class="{
                        'reports-mobile-card--delete-mode': deleteMode,
                        'reports-mobile-card--selected': isReportSelected(report),
                        'reports-mobile-card--annulled': report.is_annulled
                    }"
                    :data-report-id="report.shift_id"
                    @click="openReport(report)"
                    @keyup.enter="openReport(report)"
                    @keyup.space.prevent="openReport(report)"
                >
                    <div class="reports-mobile-card__top">
                        <div class="reports-mobile-card__id">
                            <b-form-checkbox
                                v-if="deleteMode"
                                :checked="isReportSelected(report)"
                                :disabled="!isReportSelected(report) && !canSelectReportForDelete(report)"
                                aria-label="Выбрать отчёт для аннулирования"
                                @click.native.stop
                                @change="checked => setReportDeleteSelected(report, checked)"
                            />
                            <strong>№{{ report.shift_id }}</strong>
                            <i
                                v-if="report.telegram_message_id"
                                class="fab fa-telegram-plane text-info"
                                aria-hidden="true"
                            ></i>
                        </div>
                        <div class="reports-mobile-card__status-actions">
                            <b-badge v-if="report.flags && report.flags.problem" variant="danger">
                                Проблемы
                            </b-badge>
                            <b-badge v-else variant="success">Ок</b-badge>
                            <ReportActionsMenu
                                :report="report"
                                :show-photo-actions="false"
                                @upload-photos="openPhotoUpload"
                                @resend-photos="resendPhotos"
                                @resend-report="resendTelegramReport"
                                @delete-request="openSingleDeleteRequest"
                                @copy-link="copyReportLink"
                            />
                        </div>
                    </div>

                    <div class="reports-mobile-card__object">
                        {{ report.object && report.object.name ? report.object.name : '-' }}
                    </div>

                    <div class="reports-mobile-card__dates">
                        <span>
                            <i class="fas fa-calendar-day"></i>
                            {{ formatShortDate(report.date_time_begin) }}
                        </span>
                        <span v-if="report.date_time_end">
                            <i class="fas fa-calendar-check"></i>
                            до {{ formatShortDate(report.date_time_end) }}
                        </span>
                        <span
                            v-if="report.date_time_send"
                            :class="report.flags && report.flags.late ? 'reports-mobile-card__submitted--late' : ''"
                        >
                            подан {{ formatShortDate(report.date_time_send) }}
                        </span>
                    </div>

                    <div class="reports-mobile-card__meta">
                        <span><i class="fas fa-user"></i>{{ report.author_name || '-' }}</span>
                        <span><i class="fas fa-hard-hat"></i>{{ formatNumber(report.quantity_of_workers) }}</span>
                        <span><i class="fas fa-photo-video"></i>{{ report.stats.media_count }}</span>
                        <span><i class="fas fa-gas-pump"></i>{{ formatNumber(report.stats.fuel_total_value) }}</span>
                        <span><i class="fas fa-comments"></i>{{ report.stats.comments_count }}</span>
                    </div>
                    <div class="reports-mobile-card__media-statuses">
                        <span
                            v-for="item in mediaDeliveryBadges(report)"
                            :key="item.key"
                            class="reports-media-status"
                            :class="`reports-media-status--${item.variant}`"
                        >
                            {{ item.text }}
                        </span>
                    </div>
                    <div
                        v-if="canManageReportPhotos(report)"
                        class="reports-mobile-card__media-actions"
                        @click.stop
                    >
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            class="reports-media-action-button"
                            @click="openPhotoUpload(report)"
                        >
                            <i class="fas fa-cloud-upload-alt"></i>
                            Догрузить фото
                        </b-button>
                        <b-button
                            v-if="canResendReportPhotos(report)"
                            size="sm"
                            variant="outline-warning"
                            class="reports-media-action-button"
                            @click="resendPhotos(report)"
                        >
                            <i class="fas fa-paper-plane"></i>
                            Отправить неотправленные
                        </b-button>
                    </div>
                    <ReportBadges :report="report" compact />
                    <div class="reports-mobile-card__actions">
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            class="reports-mobile-card__details"
                            @click.stop="openReportDetails(report)"
                        >
                            Подробнее
                        </b-button>
                    </div>
                </article>
            </div>

            <div v-if="!loading && !items.length" class="text-center text-muted py-4">
                Отчеты не найдены
            </div>
        </div>

        <div v-if="total > perPage" class="reports-pagination">
            <b-pagination
                v-model="pageModel"
                :total-rows="total"
                :per-page="perPage"
                size="sm"
                :limit="isMobile ? 3 : 7"
                @change="onPageChange"
            />
        </div>

        <b-modal
            v-model="accessSettingsVisible"
            title="Настройки отчётов"
            size="xl"
            ok-title="Сохранить"
            cancel-title="Отмена"
            :ok-disabled="accessSettingsLoading || objectAccessSaving"
            @ok="saveAccessSettings"
        >
            <ReportTextQualitySettings v-if="accessSettingsVisible" />
            <b-alert v-if="accessSettingsError" show variant="danger">
                {{ accessSettingsError }}
            </b-alert>

            <b-form-checkbox v-model="accessConfig.enabled" switch class="mb-3">
                Доступ к отчётам включен
            </b-form-checkbox>

            <b-tabs content-class="pt-3">
                <b-tab title="Все объекты" active>
                    <multiple-participants-config
                        v-model="accessConfig.full_access"
                        :users="participantUsers"
                        :departments="participantDepartments"
                        participant-label="получателя доступа"
                        :allowed-types="fullAccessTypes"
                        :show-fallback-option="false"
                        :show-priority="false"
                        empty-text="Доступ ко всем отчётам не настроен"
                        hint-text="Эти участники видят отчёты по всем объектам"
                    />
                </b-tab>
                <b-tab title="По назначению">
                    <multiple-participants-config
                        v-model="accessConfig.object_access"
                        :users="participantUsers"
                        :departments="participantDepartments"
                        participant-label="объектной роли"
                        :show-object-roles="true"
                        :allowed-types="objectAccessTypes"
                        :object-role-options="reportObjectRoleOptions"
                        :show-fallback-option="false"
                        :show-priority="false"
                        empty-text="Автоматический доступ по объектным ролям отключен"
                        hint-text="Эти роли видят только отчёты объектов, где они назначены"
                    />
                </b-tab>
                <b-tab title="Конкретные объекты">
                    <b-row>
                        <b-col cols="12" md="5" class="mb-3 mb-md-0">
                            <b-form-input
                                v-model="objectAccessSearch"
                                size="sm"
                                type="search"
                                placeholder="Поиск объекта"
                                autocomplete="off"
                            />
                            <div v-if="objectAccessLoading" class="text-center py-3">
                                <b-spinner small variant="secondary" />
                            </div>
                            <div v-else class="reports-object-access-list mt-2">
                                <b-button
                                    v-for="object in filteredObjectAccessObjects"
                                    :key="object.building_process_id"
                                    type="button"
                                    block
                                    size="sm"
                                    :variant="selectedObjectAccessId === object.building_process_id ? 'info' : 'outline-secondary'"
                                    class="text-left mb-2 reports-object-access-button"
                                    @click="selectObjectAccess(object)"
                                >
                                    <div class="d-flex justify-content-between align-items-start">
                                        <span class="font-weight-bold reports-object-access-name">
                                            {{ object.name }}
                                        </span>
                                        <i
                                            v-if="hasObjectAccessConfig(object)"
                                            class="fas fa-user-check ml-2"
                                            title="Доступ настроен"
                                        ></i>
                                    </div>
                                    <small class="d-block">
                                        Отчёты: {{ object.reports_count || 0 }} ·
                                        Фото: {{ object.media_count || 0 }} ·
                                        {{ object.is_active ? 'В работе' : 'Архив' }}
                                    </small>
                                </b-button>
                                <div
                                    v-if="!filteredObjectAccessObjects.length"
                                    class="text-muted text-center py-3 border rounded"
                                >
                                    Объекты не найдены
                                </div>
                            </div>
                        </b-col>
                        <b-col cols="12" md="7">
                            <div v-if="selectedObjectAccessObject">
                                <div class="font-weight-bold mb-2">
                                    {{ selectedObjectAccessObject.name }}
                                </div>
                                <multiple-participants-config
                                    :value="selectedObjectAccessConfig"
                                    :users="participantUsers"
                                    :departments="participantDepartments"
                                    participant-label="получателя доступа"
                                    :show-object-roles="true"
                                    :allowed-types="specificObjectAccessTypes"
                                    :object-role-options="reportObjectRoleOptions"
                                    :show-fallback-option="false"
                                    :show-priority="false"
                                    empty-text="Для этого объекта отдельный доступ к отчётам не настроен"
                                    hint-text="Эти участники видят отчёты только выбранного объекта"
                                    @input="onSelectedObjectAccessConfigInput"
                                />
                                <div class="d-flex justify-content-end mt-2">
                                    <b-button
                                        size="sm"
                                        variant="success"
                                        :disabled="objectAccessSaving || !selectedObjectAccessDirty"
                                        @click="saveSelectedObjectAccess"
                                    >
                                        Сохранить доступ объекта
                                    </b-button>
                                </div>
                            </div>
                            <div v-else class="text-muted text-center py-3 border rounded">
                                Выберите объект слева
                            </div>
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>
        </b-modal>

        <ReportPhotoUploadModal
            v-model="photoUploadModalVisible"
            :report="photoUploadReport"
            @uploaded="onPhotosUploaded"
        />

        <ReportDeleteRequestModal
            v-model="deleteRequestModalVisible"
            :reports="deleteRequestReports"
            @created="onDeleteRequestCreated"
        />
    </div>
</template>

<script>
import ReportBadges from './ReportBadges.vue';
import ReportTextQualitySettings from './ReportTextQualitySettings.vue';
import ReportActionsMenu from './ReportActionsMenu.vue';
import ReportDeleteRequestModal from './ReportDeleteRequestModal.vue';
import ReportPhotoUploadModal from './ReportPhotoUploadModal.vue';
import {buildMediaDeliveryBadges, hasUnsentPhotosToResend} from './reportMediaStatus';
import {canRequestReportAnnulment} from './reportAnnulmentAccess';
import TelegramUserLink from '@/views/Requests/RequestCard/components/TelegramUserLink.vue';
import MultipleParticipantsConfig from '@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue';
import {formatPhone, phoneHref} from '@/utils/phone';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

function defaultAccessConfig() {
    return {
        enabled: true,
        full_access: {participants: []},
        object_access: {
            participants: [
                {type: 'object_role', roles: ['main_staff', 'curator', 'has_access']}
            ]
        }
    };
}

function defaultObjectAccessConfig() {
    return {participants: []};
}

function cloneJson(value) {
    return JSON.parse(JSON.stringify(value || {}));
}

export default {
    name: 'Reports',
    components: {
        ReportBadges,
        ReportActionsMenu,
        ReportDeleteRequestModal,
        ReportPhotoUploadModal,
        ReportTextQualitySettings,
        TelegramUserLink,
        MultipleParticipantsConfig
    },
    data() {
        return {
            fields: [
                {key: 'shift_id', label: 'Отчёт', sortable: true, class: 'reports-id-col'},
                {key: 'date_time_begin', label: 'Смена', sortable: true, class: 'reports-date-col'},
                {key: 'object', label: 'Объект', sortable: true, class: 'reports-object-col'},
                {key: 'author', label: 'Автор', sortable: true, class: 'reports-author-col'},
                {key: 'quantity_of_workers', label: 'Рабочих на смене', sortable: true, class: 'reports-workers-col'},
                {key: 'media', label: 'Фото', sortable: true, class: 'reports-media-col'},
                {key: 'fuel', label: 'Топливо', sortable: true, class: 'reports-fuel-col'},
                {key: 'issues', label: 'Индикаторы', class: 'reports-issues-col'},
                {key: 'comments', label: '', class: 'reports-comments-col'},
                {key: 'actions', label: '', class: 'reports-actions-col', tdClass: 'reports-actions-cell'}
            ],
            issueOptions: [
                {value: 'all', text: 'Все'},
                {value: 'problem', text: 'Проблемные'},
                {value: 'late', text: 'Просрочка'},
                {value: 'no_photo', text: 'Фото не поданы'},
                {value: 'system_photo_issue', text: 'Сбой доставки фото'},
                {value: 'no_fuel', text: 'Без топлива'},
                {value: 'no_telegram', text: 'Нет Telegram ID'}
            ],
            reportStatusValues: [
                {value: 'all', text: 'Все', variant: 'secondary'},
                {value: 'active', text: 'Действующие', variant: 'success'},
                {value: 'annulled', text: 'Аннулированные', variant: 'danger'}
            ],
            reloadTimer: null,
            highlightCleanupTimer: null,
            highlightAnimationTimer: null,
            suppressHighlightWatch: false,
            filtersVisible: false,
            photoUploadModalVisible: false,
            photoUploadReport: null,
            mediaPreviewCache: {},
            mediaPreviewLoading: {},
            deleteMode: false,
            selectedReportIdsForDelete: [],
            deleteRequestModalVisible: false,
            accessSettingsVisible: false,
            accessSettingsLoading: false,
            accessSettingsError: null,
            accessConfig: defaultAccessConfig(),
            objectAccessObjects: [],
            objectAccessSearch: '',
            objectAccessLoading: false,
            objectAccessSaving: false,
            selectedObjectAccessId: null,
            selectedObjectAccessConfig: defaultObjectAccessConfig(),
            selectedObjectAccessDirty: false,
            fullAccessTypes: ['all', 'specific_users', 'department'],
            objectAccessTypes: ['object_role'],
            specificObjectAccessTypes: ['specific_users', 'department', 'object_role'],
            reportObjectRoleOptions: [
                {value: 'main_staff', text: 'Прораб'},
                {value: 'curator', text: 'Куратор'},
                {value: 'has_access', text: 'Имеющий доступ'}
            ]
        };
    },
    computed: {
        items() {
            return this.$store.state.reports.items;
        },
        loading() {
            return this.$store.state.reports.loading;
        },
        total() {
            return this.$store.state.reports.total;
        },
        perPage() {
            return this.$store.state.reports.perPage;
        },
        counts() {
            return this.$store.state.reports.counts;
        },
        reportStatusOptions() {
            const counts = this.counts || {};
            return this.reportStatusValues.map(option => ({
                ...option,
                count: option.value === 'all'
                    ? Number(counts.active || 0) + Number(counts.annulled || 0)
                    : Number(counts[option.value] || 0)
            }));
        },
        filterOptions() {
            return this.$store.state.reports.filterOptions;
        },
        tableFields() {
            if (!this.deleteMode) {
                return this.fields;
            }
            return [{
                key: 'delete_select',
                label: '',
                class: 'reports-select-col',
                tdClass: 'reports-select-cell'
            }].concat(this.fields);
        },
        buildingProcessOptions() {
            return [{value: 'all', text: 'Все объекты'}].concat(this.filterOptions.building_processes || []);
        },
        authorOptions() {
            return [{value: 'all', text: 'Все авторы'}].concat(this.filterOptions.authors || []);
        },
        isMobile() {
            return this.$store.getters.isMobile;
        },
        currentUser() {
            return this.$store.getters.currentUser || {};
        },
        currentUserId() {
            return Number(this.currentUser.userId || this.currentUser.user_id || this.currentUser.id);
        },
        isAdminLikeUser() {
            const roles = Array.isArray(this.currentUser.roles) ? this.currentUser.roles : [];
            return this.$store.getters.isAdmin || roles.indexOf('Top') !== -1;
        },
        authQuery() {
            const token = this.$store && this.$store.state ? this.$store.state.token : null;
            return token ? `?gjuid=${encodeURIComponent(token)}` : '';
        },
        canManageReportsAccess() {
            const roles = Array.isArray(this.currentUser.roles) ? this.currentUser.roles : [];
            return roles.indexOf('Admin') !== -1 || roles.indexOf('Top') !== -1;
        },
        participantUsers() {
            return (this.$store.state.users || [])
                .filter(user => user && user.user_id)
                .map(user => ({
                    value: user.user_id,
                    text: user.full_name || `${user.last_name || ''} ${user.first_name || ''}`.trim()
                }));
        },
        participantDepartments() {
            return (this.$store.state.departments || [])
                .filter(department => department && department.id)
                .map(department => ({
                    value: department.id,
                    text: department.name
                }));
        },
        filteredObjectAccessObjects() {
            const query = String(this.objectAccessSearch || '').trim().toLowerCase();
            if (!query) {
                return this.objectAccessObjects;
            }
            return this.objectAccessObjects.filter(object => {
                return String(object.name || '').toLowerCase().indexOf(query) !== -1;
            });
        },
        selectedObjectAccessObject() {
            return this.objectAccessObjects.find(object => {
                return object.building_process_id === this.selectedObjectAccessId;
            }) || null;
        },
        pageModel: {
            get() { return this.$store.state.reports.page; },
            set(value) { this.$store.commit('reports/setPage', value); }
        },
        queryModel: {
            get() { return this.$store.state.reports.query; },
            set(value) {
                this.$store.commit('reports/setQuery', value);
                this.scheduleReload();
            }
        },
        objectStatusModel() {
            return this.$store.state.reports.objectStatus || 'active';
        },
        reportStatusModel: {
            get() { return this.$store.state.reports.reportStatus || 'all'; },
            set(value) {
                this.$store.commit('reports/setReportStatus', value);
                this.reload();
            }
        },
        issueModel: {
            get() { return this.$store.state.reports.issue; },
            set(value) {
                this.$store.commit('reports/setIssue', value);
                this.reload();
            }
        },
        buildingProcessModel: {
            get() { return this.$store.state.reports.buildingProcessId; },
            set(value) {
                this.$store.commit('reports/setBuildingProcessId', value);
                this.reload();
            }
        },
        authorModel: {
            get() { return this.$store.state.reports.authorId; },
            set(value) {
                this.$store.commit('reports/setAuthorId', value);
                this.reload();
            }
        },
        dateFromModel: {
            get() { return this.$store.state.reports.dateFrom; },
            set(value) {
                this.$store.commit('reports/setDateFrom', value);
                this.reload();
            }
        },
        dateToModel: {
            get() { return this.$store.state.reports.dateTo; },
            set(value) {
                this.$store.commit('reports/setDateTo', value);
                this.reload();
            }
        },
        sortByModel: {
            get() { return this.$store.state.reports.sortBy; },
            set(value) { this.$store.commit('reports/setSortBy', value); }
        },
        sortDescModel: {
            get() { return this.$store.state.reports.sortDesc; },
            set(value) { this.$store.commit('reports/setSortDesc', value); }
        },
        highlightId() {
            return this.$route.query && this.$route.query.highlight
                ? String(this.$route.query.highlight)
                : '';
        },
        selectedReportsForDelete() {
            const ids = this.selectedReportIdsForDelete.map(id => Number(id));
            return this.items.filter(item => ids.indexOf(Number(item.shift_id)) !== -1);
        },
        canRequestAnnulmentForVisibleReports() {
            return this.items.some(report => this.canManageReportAnnulment(report));
        },
        selectedDeleteProcessId() {
            const report = this.selectedReportsForDelete[0];
            return report ? Number(report.building_process_id || 0) : 0;
        },
        deleteRequestReports() {
            return this.selectedReportsForDelete;
        },
        activeFilterChips() {
            const chips = [];
            if (this.queryModel) {
                chips.push({
                    key: 'query',
                    text: `Поиск: ${this.queryModel}`,
                    variant: 'primary'
                });
            }
            if (this.objectStatusModel === 'archive') {
                chips.push({
                    key: 'objectStatus',
                    text: 'Архивные объекты',
                    variant: 'dark'
                });
            }
            if (this.reportStatusModel !== 'all') {
                chips.push({
                    key: 'reportStatus',
                    text: this.reportStatusModel === 'annulled' ? 'Аннулированные отчёты' : 'Действующие отчёты',
                    variant: this.reportStatusModel === 'annulled' ? 'danger' : 'success'
                });
            }
            if (this.issueModel !== 'all') {
                chips.push({
                    key: 'issue',
                    text: this.getOptionText(this.issueOptions, this.issueModel),
                    variant: this.issueVariant(this.issueModel)
                });
            }
            if (this.buildingProcessModel !== 'all') {
                chips.push({
                    key: 'buildingProcess',
                    text: `Объект: ${this.getOptionText(this.buildingProcessOptions, this.buildingProcessModel)}`,
                    variant: 'info'
                });
            }
            if (this.authorModel !== 'all') {
                chips.push({
                    key: 'author',
                    text: `Автор: ${this.getOptionText(this.authorOptions, this.authorModel)}`,
                    variant: 'secondary'
                });
            }
            if (this.dateFromModel) {
                chips.push({
                    key: 'dateFrom',
                    text: `С ${this.formatFilterDate(this.dateFromModel)}`,
                    variant: 'dark'
                });
            }
            if (this.dateToModel) {
                chips.push({
                    key: 'dateTo',
                    text: `По ${this.formatFilterDate(this.dateToModel)}`,
                    variant: 'dark'
                });
            }
            return chips;
        }
    },
    watch: {
        highlightId(value) {
            if (this.suppressHighlightWatch) {
                return;
            }
            if (value) {
                this.highlightRowOnce();
            }
        }
    },
    methods: {
        formatPhone,
        phoneHref,
        async reload() {
            this.selectedReportIdsForDelete = [];
            await this.$store.dispatch('reports/fetchReports', {resetPage: true});
        },
        scheduleReload() {
            clearTimeout(this.reloadTimer);
            this.reloadTimer = setTimeout(this.reload, 350);
        },
        async setObjectStatus(value) {
            const nextValue = value === 'archive' ? 'archive' : 'active';
            if (this.objectStatusModel === nextValue) {
                return;
            }
            this.selectedReportIdsForDelete = [];
            this.$store.commit('reports/setObjectStatus', nextValue);
            this.$store.commit('reports/setBuildingProcessId', 'all');
            this.$store.commit('reports/setAuthorId', 'all');
            await Promise.all([
                this.$store.dispatch('reports/fetchFilterOptions').catch(() => {}),
                this.$store.dispatch('reports/fetchReports', {resetPage: true})
            ]);
        },
        onPageChange(page) {
            this.selectedReportIdsForDelete = [];
            this.$store.dispatch('reports/fetchReports', {page});
        },
        onSortChanged(ctx) {
            this.selectedReportIdsForDelete = [];
            this.$store.commit('reports/setSortBy', this.mapSortField(ctx.sortBy));
            this.$store.commit('reports/setSortDesc', ctx.sortDesc);
            this.reload();
        },
        toggleDeleteMode() {
            if (this.deleteMode) {
                this.cancelDeleteMode();
                return;
            }
            this.deleteMode = true;
            this.selectedReportIdsForDelete = [];
        },
        cancelDeleteMode() {
            this.deleteMode = false;
            this.selectedReportIdsForDelete = [];
        },
        isReportSelected(report) {
            if (!report) return false;
            return this.selectedReportIdsForDelete
                .map(id => Number(id))
                .indexOf(Number(report.shift_id)) !== -1;
        },
        canSelectReportForDelete(report) {
            if (!this.canManageReportAnnulment(report)) return false;
            if (!this.selectedDeleteProcessId) return true;
            return Number(report.building_process_id || 0) === this.selectedDeleteProcessId;
        },
        canManageReportAnnulment(report) {
            return canRequestReportAnnulment(
                report,
                this.currentUser,
                this.$store.getters.isAdmin
            );
        },
        setReportDeleteSelected(report, checked) {
            if (!report) return;
            const reportId = Number(report.shift_id);
            if (!reportId) return;
            if (checked) {
                if (!this.canSelectReportForDelete(report)) {
                    this.$bvToast.toast('В одной заявке можно выбрать отчёты только одного объекта', {
                        title: 'Аннулирование отчётов',
                        variant: 'warning',
                        solid: true
                    });
                    return;
                }
                if (!this.isReportSelected(report)) {
                    this.selectedReportIdsForDelete = this.selectedReportIdsForDelete.concat([reportId]);
                }
            } else {
                this.selectedReportIdsForDelete = this.selectedReportIdsForDelete
                    .filter(id => Number(id) !== reportId);
            }
        },
        toggleReportDeleteSelection(report) {
            this.setReportDeleteSelected(report, !this.isReportSelected(report));
        },
        reportRowClass(item) {
            if (!item) return '';
            return [
                item.is_annulled ? 'reports-row--annulled' : '',
                this.deleteMode && this.isReportSelected(item) ? 'reports-row--delete-selected' : ''
            ].filter(Boolean).join(' ');
        },
        openDeleteRequestModal() {
            if (!this.selectedReportsForDelete.length) {
                this.$bvToast.toast('Выберите хотя бы один отчёт', {
                    title: 'Аннулирование отчётов',
                    variant: 'warning',
                    solid: true
                });
                return;
            }
            this.deleteRequestModalVisible = true;
        },
        async onDeleteRequestCreated({request, approved, sent}) {
            const requestId = request && request.id ? request.id : null;
            const message = approved
                ? `Заявка №${requestId} согласована, выбранные отчёты аннулированы`
                : sent
                    ? `Заявка №${requestId} отправлена куратору`
                    : `Заявка №${requestId} сохранена как черновик`;
            this.$bvToast.toast(message, {
                title: 'Аннулирование отчётов',
                variant: approved ? 'success' : 'info',
                solid: true
            });
            this.cancelDeleteMode();
            await this.$store.dispatch('reports/fetchReports').catch(() => {});
        },
        mapSortField(value) {
            const map = {
                object: 'object_name',
                author: 'author_name',
                media: 'media_count',
                fuel: 'fuel_total_value'
            };
            return map[value] || value || 'date_time_begin';
        },
        async resetFilters() {
            this.$store.commit('reports/resetFilters');
            await this.$store.dispatch('reports/fetchFilterOptions').catch(() => {});
            await this.reload();
        },
        async clearFilterChip(key) {
            if (key === 'objectStatus') {
                await this.setObjectStatus('active');
                return;
            }
            if (key === 'query') this.$store.commit('reports/setQuery', '');
            if (key === 'reportStatus') this.$store.commit('reports/setReportStatus', 'all');
            if (key === 'issue') this.$store.commit('reports/setIssue', 'all');
            if (key === 'buildingProcess') this.$store.commit('reports/setBuildingProcessId', 'all');
            if (key === 'author') this.$store.commit('reports/setAuthorId', 'all');
            if (key === 'dateFrom') this.$store.commit('reports/setDateFrom', '');
            if (key === 'dateTo') this.$store.commit('reports/setDateTo', '');
            await this.reload();
        },
        async markReportForBrowserBack(report) {
            if (!report || !report.shift_id || this.$route.path !== '/reports') {
                return;
            }
            const currentHighlight = this.$route.query && this.$route.query.highlight;
            if (String(currentHighlight || '') === String(report.shift_id)) {
                return;
            }
            const query = {
                ...this.$route.query,
                highlight: String(report.shift_id)
            };
            this.suppressHighlightWatch = true;
            await this.$router.replace({
                path: this.$route.path,
                query
            }).catch(() => {});
            await this.$nextTick();
            this.suppressHighlightWatch = false;
        },
        async openReport(report) {
            if (this.deleteMode) {
                this.toggleReportDeleteSelection(report);
                return;
            }
            if (!report || !report.shift_id) return;
            await this.markReportForBrowserBack(report);
            this.$router.push(`/reports/${report.shift_id}`);
        },
        async openReportDetails(report) {
            if (!report || !report.shift_id) return;
            await this.markReportForBrowserBack(report);
            this.$router.push(`/reports/${report.shift_id}`);
        },
        openPhotoUpload(report) {
            if (!report || !report.shift_id) return;
            this.photoUploadReport = report;
            this.photoUploadModalVisible = true;
        },
        canManageReportPhotos(report) {
            if (!report || report.is_annulled || !report.object) {
                return false;
            }
            if (this.isAdminLikeUser) {
                return true;
            }
            if (!this.currentUserId) {
                return false;
            }
            const object = report.object;
            const allowedIds = [
                object.curator_id,
                object.main_staff_id
            ].concat((object.has_access_users || []).map(user => user.user_id));

            return allowedIds.some(id => Number(id) === this.currentUserId);
        },
        canResendReportPhotos(report) {
            return this.canManageReportPhotos(report) && hasUnsentPhotosToResend(report);
        },
        async onPhotosUploaded({report, uploadedCount}) {
            const id = report && report.shift_id;
            if (id) {
                this.$delete(this.mediaPreviewCache, String(id));
            }
            this.$bvToast.toast(`Принято файлов: ${uploadedCount || 0}. Отправка в Telegram запущена`, {
                title: 'Догрузка фото',
                variant: 'success',
                solid: true
            });
            await this.$store.dispatch('reports/fetchReports').catch(() => {});
        },
        async resendPhotos(report) {
            if (!report || !report.shift_id) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Отправить неотправленные фото отчёта №${report.shift_id} в Telegram?`,
                {
                    title: 'Отправка неотправленных фото',
                    okTitle: 'Отправить',
                    cancelTitle: 'Отмена',
                    okVariant: 'primary'
                }
            );
            if (!confirmed) return;

            try {
                const result = await this.$store.dispatch('reports/resendPhotos', report.shift_id);
                notifyInstallPromotionSuccess({source: 'report-photos-resent'});
                this.$bvToast.toast(
                    `Запущена отправка ${result && result.mediaCount ? result.mediaCount : ''} фото`,
                    {
                        title: 'Отправка неотправленных фото',
                        variant: 'info',
                        solid: true
                    }
                );
            } catch (err) {
                this.$bvToast.toast(
                    err && err.response && err.response.data && err.response.data.msg
                        ? err.response.data.msg
                        : 'Не удалось запустить отправку неотправленных фото',
                    {
                        title: 'Отправка неотправленных фото',
                        variant: 'danger',
                        solid: true
                    }
                );
            }
        },
        async resendTelegramReport(report) {
            if (!report || !report.shift_id) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Отправить отчёт №${report.shift_id} новым сообщением в Telegram?`,
                {
                    title: 'Повторная отправка отчёта',
                    okTitle: 'Отправить',
                    cancelTitle: 'Отмена',
                    okVariant: 'primary'
                }
            );
            if (!confirmed) return;

            try {
                await this.$store.dispatch('reports/resendTelegramReport', report.shift_id);
                notifyInstallPromotionSuccess({source: 'report-resent'});
                this.$bvToast.toast('Повторная отправка отчёта запущена', {
                    title: 'Telegram',
                    variant: 'info',
                    solid: true
                });
                await this.$store.dispatch('reports/fetchReports').catch(() => {});
            } catch (err) {
                this.$bvToast.toast(
                    err && err.response && err.response.data && err.response.data.msg
                        ? err.response.data.msg
                        : 'Не удалось запустить повторную отправку отчёта',
                    {
                        title: 'Telegram',
                        variant: 'danger',
                        solid: true
                    }
                );
            }
        },
        openSingleDeleteRequest(report) {
            if (!report || !report.shift_id || !this.canManageReportAnnulment(report)) return;
            this.selectedReportIdsForDelete = [report.shift_id];
            this.deleteRequestModalVisible = true;
        },
        async copyReportLink(report) {
            if (!report || !report.shift_id) return;
            const url = `${window.location.origin}/reports/${report.shift_id}`;
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(url);
                } else {
                    const input = document.createElement('textarea');
                    input.value = url;
                    input.setAttribute('readonly', 'readonly');
                    input.style.position = 'fixed';
                    input.style.opacity = '0';
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                }
                this.$bvToast.toast('Ссылка на отчёт скопирована', {
                    title: 'Отчёты',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                this.$bvToast.toast(url, {
                    title: 'Ссылка на отчёт',
                    variant: 'warning',
                    solid: true
                });
            }
        },
        previewKey(report) {
            return report && report.shift_id ? String(report.shift_id) : '';
        },
        async loadMediaPreview(report) {
            const key = this.previewKey(report);
            if (!key || this.mediaPreviewCache[key] || this.mediaPreviewLoading[key]) {
                return;
            }
            this.$set(this.mediaPreviewLoading, key, true);
            try {
                const data = await this.$store.dispatch('reports/fetchMediaPreview', {
                    id: report.shift_id,
                    limit: 8
                });
                this.$set(this.mediaPreviewCache, key, {
                    items: data && Array.isArray(data.items) ? data.items : [],
                    total: data && data.total != null ? Number(data.total) : 0
                });
            } catch (err) {
                this.$set(this.mediaPreviewCache, key, {items: [], total: 0});
            } finally {
                this.$set(this.mediaPreviewLoading, key, false);
            }
        },
        isMediaPreviewLoading(report) {
            return Boolean(this.mediaPreviewLoading[this.previewKey(report)]);
        },
        mediaPreviewItems(report) {
            const payload = this.mediaPreviewCache[this.previewKey(report)];
            return payload && Array.isArray(payload.items) ? payload.items : [];
        },
        mediaPreviewTotal(report) {
            const payload = this.mediaPreviewCache[this.previewKey(report)];
            if (payload && payload.total != null) {
                return Number(payload.total) || 0;
            }
            return report && report.stats ? Number(report.stats.media_count) || 0 : 0;
        },
        mediaId(file) {
            return file && file.id ? file.id : null;
        },
        mediaThumbnailUrl(file) {
            const id = this.mediaId(file);
            return id ? `/images/mini/${id}${this.authQuery}` : '';
        },
        mediaUrl(file) {
            const id = this.mediaId(file);
            return id ? `/images/${id}${this.authQuery}` : '';
        },
        isMediaVideo(file) {
            const name = String(file && (file.name || file.filename) || '').toLowerCase();
            const mimeType = String(file && file.mimeType || '').toLowerCase();
            return Boolean(file && file.isVideo) ||
                mimeType.indexOf('video/') === 0 ||
                /\.(mp4|mov|m4v|avi|webm|mkv|3gp)$/i.test(name);
        },
        isMediaImage(file) {
            const name = String(file && (file.name || file.filename) || '').toLowerCase();
            const mimeType = String(file && file.mimeType || '').toLowerCase();
            return !this.isMediaVideo(file) && (
                Boolean(file && file.isImage) ||
                mimeType.indexOf('image/') === 0 ||
                /\.(png|jpe?g|jfif|gif|webp|bmp|heic|heif)$/i.test(name)
            );
        },
        openMediaPreview(report, index) {
            const files = this.mediaPreviewItems(report).map((file, itemIndex) => ({
                id: this.mediaId(file) || itemIndex,
                name: file.name || file.filename || `Файл ${itemIndex + 1}`,
                url: this.mediaUrl(file),
                downloadUrl: this.mediaUrl(file),
                thumbnailUrl: this.isMediaImage(file) ? this.mediaThumbnailUrl(file) : null,
                mimeType: file.mimeType,
                isImage: this.isMediaImage(file),
                isVideo: this.isMediaVideo(file),
                galleryShiftId: report.shift_id,
                galleryShiftTitle: `Отчёт №${report.shift_id}`,
                reportAnnulled: Boolean(report.is_annulled),
                reportAnnulmentReason: report.annulment && report.annulment.reason || '',
                raw: file
            }));
            if (this.$fileViewer) {
                this.$fileViewer.open(files, {
                    startIndex: index,
                    source: 'reports-preview',
                    continuous: true
                });
                return;
            }
            if (files[index] && files[index].url) {
                window.open(files[index].url, '_blank');
            }
        },
        scheduleHighlightCleanup(id) {
            if (!id) return;
            clearTimeout(this.highlightCleanupTimer);
            this.highlightCleanupTimer = setTimeout(() => {
                if (!this.highlightId || String(this.highlightId) !== String(id)) return;
                const query = {...this.$route.query};
                delete query.highlight;
                this.$router.replace({path: this.$route.path, query}).catch(() => {});
            }, 2100);
        },
        async highlightRowOnce() {
            const id = this.highlightId;
            if (!id) return;
            await this.$nextTick();
            await this.$nextTick();
            const marker = this.$el.querySelector(`#report_row_${id}`);
            const tableRow = marker && marker.closest ? marker.closest('tr') : null;
            const mobileCard = this.$el.querySelector(`.reports-mobile-card[data-report-id="${id}"]`);
            const target = tableRow || mobileCard;
            if (!target) {
                this.scheduleHighlightCleanup(id);
                return;
            }

            if (target.scrollIntoView) {
                target.scrollIntoView({block: 'center', inline: 'nearest'});
            }
            clearTimeout(this.highlightAnimationTimer);
            target.classList.remove('reports-row--highlight', 'reports-mobile-card--highlight');
            // Force restart of the CSS animation without touching Vue state or table data.
            void target.offsetWidth;
            target.classList.add(tableRow ? 'reports-row--highlight' : 'reports-mobile-card--highlight');
            this.highlightAnimationTimer = setTimeout(() => {
                target.classList.remove('reports-row--highlight', 'reports-mobile-card--highlight');
            }, 1900);
            this.scheduleHighlightCleanup(id);
        },
        formatDate(value) {
            return value ? this.$moment(value).format('DD.MM.YYYY dd HH:mm') : '-';
        },
        formatShortDate(value) {
            return value ? this.$moment(value).format('DD.MM dd HH:mm') : '-';
        },
        formatTimeRange(report) {
            if (!report || !report.date_time_end) return '';
            return `до ${this.$moment(report.date_time_end).format('DD.MM.YYYY dd HH:mm')}`;
        },
        mediaDeliveryBadges(report) {
            return buildMediaDeliveryBadges(report);
        },
        formatNumber(value) {
            const number = Number(value);
            if (!Number.isFinite(number)) return '0';
            return number.toLocaleString('ru-RU', {maximumFractionDigits: 2});
        },
        formatFilterDate(value) {
            return value ? this.$moment(value).format('DD.MM.YYYY') : '';
        },
        getOptionText(options, value) {
            const option = (options || []).find(item => String(item.value) === String(value));
            return option ? option.text : value;
        },
        issueVariant(value) {
            if (['problem', 'late', 'no_photo'].indexOf(value) !== -1) return 'danger';
            if (value === 'system_photo_issue' || value === 'no_telegram') return 'warning';
            if (value === 'no_fuel') return 'info';
            return 'secondary';
        },
        normalizeAccessConfig(config) {
            const source = config || {};
            return {
                enabled: source.enabled !== false,
                full_access: source.full_access || {participants: []},
                object_access: source.object_access || {participants: []}
            };
        },
        normalizeObjectAccessConfig(config) {
            const source = config || {};
            return {
                participants: Array.isArray(source.participants)
                    ? source.participants
                    : []
            };
        },
        hasObjectAccessConfig(object) {
            const config = object && object.report_access_config ? object.report_access_config : null;
            return Boolean(config && Array.isArray(config.participants) && config.participants.length);
        },
        async loadReportObjectAccess() {
            this.objectAccessLoading = true;
            try {
                const data = await this.$store.dispatch('reports/fetchObjectAccess', {
                    query: this.objectAccessSearch || undefined
                });
                this.objectAccessObjects = data && Array.isArray(data.items)
                    ? data.items.map(object => ({
                        ...object,
                        report_access_config: this.normalizeObjectAccessConfig(object.report_access_config)
                    }))
                    : [];

                const selectedStillExists = this.objectAccessObjects.some(object => {
                    return object.building_process_id === this.selectedObjectAccessId;
                });
                if (!selectedStillExists) {
                    const firstObject = this.objectAccessObjects[0] || null;
                    await this.selectObjectAccess(firstObject, {skipSave: true});
                } else if (!this.selectedObjectAccessDirty) {
                    const selected = this.selectedObjectAccessObject;
                    this.selectedObjectAccessConfig = cloneJson(
                        this.normalizeObjectAccessConfig(selected.report_access_config)
                    );
                }
            } finally {
                this.objectAccessLoading = false;
            }
        },
        async selectObjectAccess(object, options = {}) {
            if (!object) {
                this.selectedObjectAccessId = null;
                this.selectedObjectAccessConfig = defaultObjectAccessConfig();
                this.selectedObjectAccessDirty = false;
                return;
            }

            if (this.selectedObjectAccessDirty && !options.skipSave) {
                await this.saveSelectedObjectAccess();
            }

            this.selectedObjectAccessId = object.building_process_id;
            this.selectedObjectAccessConfig = cloneJson(
                this.normalizeObjectAccessConfig(object.report_access_config)
            );
            this.selectedObjectAccessDirty = false;
        },
        onSelectedObjectAccessConfigInput(config) {
            this.selectedObjectAccessConfig = this.normalizeObjectAccessConfig(config);
            this.selectedObjectAccessDirty = true;
        },
        async saveSelectedObjectAccess() {
            const selected = this.selectedObjectAccessObject;
            if (!selected) {
                return;
            }

            this.accessSettingsError = null;
            this.objectAccessSaving = true;
            try {
                const data = await this.$store.dispatch('reports/updateObjectAccess', {
                    id: selected.building_process_id,
                    accessConfig: this.selectedObjectAccessConfig
                });
                const savedConfig = this.normalizeObjectAccessConfig(data.access_config);
                const index = this.objectAccessObjects.findIndex(object => {
                    return object.building_process_id === selected.building_process_id;
                });
                if (index !== -1) {
                    this.$set(this.objectAccessObjects, index, {
                        ...this.objectAccessObjects[index],
                        report_access_config: savedConfig
                    });
                }
                this.selectedObjectAccessConfig = cloneJson(savedConfig);
                this.selectedObjectAccessDirty = false;
                notifyInstallPromotionSuccess({
                    source: 'report-object-access-updated'
                });
            } catch (err) {
                this.accessSettingsError = err && err.response && err.response.data && err.response.data.msg
                    ? err.response.data.msg
                    : 'Не удалось сохранить доступ объекта';
                throw err;
            } finally {
                this.objectAccessSaving = false;
            }
        },
        async openAccessSettings() {
            this.accessSettingsError = null;
            this.accessSettingsVisible = true;
            this.accessSettingsLoading = true;
            try {
                await Promise.all([
                    this.$store.dispatch('getUsers'),
                    this.$store.dispatch('getDepartments')
                ]);
                const [data] = await Promise.all([
                    this.$store.dispatch('reports/fetchAccessSettings'),
                    this.loadReportObjectAccess()
                ]);
                this.accessConfig = this.normalizeAccessConfig(data.access_config);
            } catch (err) {
                this.accessSettingsError = err && err.response && err.response.data && err.response.data.msg
                    ? err.response.data.msg
                    : 'Не удалось загрузить настройки доступа';
            } finally {
                this.accessSettingsLoading = false;
            }
        },
        async saveAccessSettings(event) {
            event.preventDefault();
            this.accessSettingsError = null;
            this.accessSettingsLoading = true;
            try {
                if (this.selectedObjectAccessDirty) {
                    await this.saveSelectedObjectAccess();
                }
                const data = await this.$store.dispatch(
                    'reports/updateAccessSettings',
                    this.accessConfig
                );
                this.accessConfig = this.normalizeAccessConfig(data.access_config);
                notifyInstallPromotionSuccess({
                    source: 'report-access-settings-updated'
                });
                this.accessSettingsVisible = false;
                await Promise.all([
                    this.$store.dispatch('reports/fetchFilterOptions').catch(() => {}),
                    this.$store.dispatch('reports/fetchReports').catch(() => {})
                ]);
            } catch (err) {
                this.accessSettingsError = err && err.response && err.response.data && err.response.data.msg
                    ? err.response.data.msg
                    : 'Не удалось сохранить настройки доступа';
            } finally {
                this.accessSettingsLoading = false;
            }
        }
    },
    async mounted() {
        await this.$store.dispatch('reports/fetchFilterOptions').catch(() => {});
        await this.$store.dispatch('reports/fetchReports');
        await this.highlightRowOnce();
    },
    beforeDestroy() {
        clearTimeout(this.reloadTimer);
        clearTimeout(this.highlightCleanupTimer);
        clearTimeout(this.highlightAnimationTimer);
    }
}
</script>

<style scoped>
.reports-page {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 48px);
    min-height: 0;
}

.reports-toolbar {
    flex: 0 0 auto;
    padding: 8px 12px 4px;
}

.reports-toolbar__top {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

.reports-toolbar__heading {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.reports-toolbar__meta {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.reports-submit-button,
.reports-access-button {
    font-weight: 700;
}

.reports-delete-mode-button {
    font-weight: 700;
}

.reports-mobile-filter-toggle {
    align-items: center;
    display: inline-flex;
    gap: 5px;
    font-weight: 700;
}

.reports-submit-button i,
.reports-access-button i {
    margin-right: 4px;
}

.reports-delete-mode-button i {
    margin-right: 4px;
}

.reports-delete-bar {
    align-items: center;
    background: #fff5f5;
    border: 1px solid #f1b0b7;
    border-radius: 6px;
    color: #721c24;
    display: flex;
    gap: 10px;
    justify-content: space-between;
    margin: 6px 0 4px;
    padding: 8px 10px;
}

.reports-delete-bar__info {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
}

.reports-delete-bar__actions {
    display: flex;
    flex: 0 0 auto;
    gap: 6px;
}

.reports-status-switch {
    background: #f1f3f5;
    border: 1px solid #cbd3dc;
    border-radius: 6px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: 32px;
    overflow: hidden;
}

.reports-status-switch__button {
    background: transparent;
    border: 0;
    color: #495057;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.1;
    min-width: 0;
    padding: 6px 7px;
}

.reports-status-switch__button + .reports-status-switch__button {
    border-left: 1px solid #cbd3dc;
}

.reports-status-switch__button--active {
    background: #0d6efd;
    color: #fff;
}

.reports-report-status-filter {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    margin-bottom: 8px;
}

.reports-report-status-filter > span {
    color: #6c757d;
    font-size: 12px;
    font-weight: 700;
    margin-right: 8px;
}

.reports-report-status-filter__button {
    align-items: center;
    background: #fff;
    border: 1px solid #cbd3dc;
    color: #495057;
    display: inline-flex;
    font-size: 12px;
    font-weight: 700;
    gap: 5px;
    min-height: 30px;
    padding: 4px 9px;
}

.reports-report-status-filter__button + .reports-report-status-filter__button {
    border-left: 0;
}

.reports-report-status-filter__button:first-of-type {
    border-radius: 5px 0 0 5px;
}

.reports-report-status-filter__button:last-child {
    border-radius: 0 5px 5px 0;
}

.reports-report-status-filter__button--active {
    background: #e9ecef;
    box-shadow: inset 0 -3px 0 #0d6efd;
    color: #212529;
}

.reports-filter-select {
    background: #fff;
    font-size: 12px;
}

.reports-filter-select >>> .vs__dropdown-toggle {
    border-color: #ced4da;
    border-radius: 4px;
    min-height: 31px;
    padding: 0 4px;
}

.reports-filter-select >>> .vs__selected {
    line-height: 1.2;
    margin: 2px 2px 0;
}

.reports-filter-select >>> .vs__search {
    font-size: 12px;
    margin: 2px 0 0;
    padding: 0 2px;
}

.reports-filter-select >>> .vs__actions {
    padding: 0 2px 0 0;
}

.reports-active-filters {
    align-items: center;
    background: #fff8e1;
    border: 1px solid #ffe08a;
    border-radius: 6px;
    color: #5c4300;
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
    gap: 5px;
    margin-bottom: 6px;
    padding: 6px 8px;
}

.reports-active-filters__label {
    align-items: center;
    display: inline-flex;
    flex: 0 0 auto;
    font-weight: 700;
    gap: 5px;
}

.reports-active-filter {
    align-items: center;
    border: 1px solid transparent;
    border-radius: 999px;
    color: #fff;
    display: inline-flex;
    font-size: 12px;
    font-weight: 700;
    gap: 4px;
    line-height: 1.2;
    min-height: 26px;
    max-width: 100%;
    padding: 3px 8px;
    white-space: nowrap;
}

.reports-active-filter__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.reports-active-filter__remove {
    margin-left: 2px;
    opacity: .85;
}

.reports-active-filter--primary {
    background: #007bff;
    border-color: #007bff;
}

.reports-active-filter--secondary {
    background: #6c757d;
    border-color: #6c757d;
}

.reports-active-filter--danger {
    background: #dc3545;
    border-color: #dc3545;
}

.reports-active-filter--success {
    background: #28a745;
    border-color: #28a745;
}

.reports-active-filter--warning {
    background: #ffc107;
    border-color: #ffc107;
    color: #212529;
}

.reports-active-filter--info {
    background: #17a2b8;
    border-color: #17a2b8;
}

.reports-active-filter--dark {
    background: #343a40;
    border-color: #343a40;
}

.reports-active-filters__reset {
    background: #fff;
    border: 1px solid #b38300;
    border-radius: 4px;
    color: #5c4300;
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    min-height: 26px;
    padding: 3px 8px;
}

.reports-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 0 12px;
}

.reports-loading {
    align-items: center;
    color: #6c757d;
    display: flex;
    gap: 8px;
    justify-content: center;
    padding: 10px;
}

.reports-table {
    cursor: pointer;
    margin-bottom: 0;
    width: 100%;
}

.reports-table,
.reports-table >>> table {
    table-layout: fixed;
}

.reports-table >>> table {
    width: 100%;
}

.reports-table >>> th,
.reports-table >>> td {
    padding-right: .35rem;
    padding-left: .35rem;
    overflow-wrap: anywhere;
    vertical-align: middle;
}

.reports-table >>> .reports-select-col {
    width: 4%;
}

.reports-table >>> .reports-id-col {
    width: 7%;
}

.reports-table >>> .reports-date-col {
    width: 11%;
}

.reports-table >>> .reports-object-col {
    width: 14%;
}

.reports-table >>> .reports-author-col {
    width: 13%;
}

.reports-table >>> .reports-workers-col {
    width: 7%;
}

.reports-table >>> .reports-media-col {
    width: 11%;
}

.reports-table >>> .reports-fuel-col {
    width: 6%;
}

.reports-table >>> .reports-issues-col {
    width: 14%;
}

.reports-table >>> .reports-comments-col {
    width: 4%;
}

.reports-table >>> .reports-actions-col {
    width: 9%;
}

.reports-table >>> tbody tr.reports-row--delete-selected > td {
    background: #fff1f2;
}

.reports-table >>> tbody tr.reports-row--annulled > td {
    background: #fff3f3;
    color: #721c24;
}

.reports-table >>> tbody tr.reports-row--annulled:hover > td {
    background: #ffe5e7;
}

.reports-select-cell {
    cursor: default;
}

.reports-row-marker {
    display: inline-block;
    height: 0;
    overflow: hidden;
    width: 0;
}

.reports-object-name {
    display: block;
    overflow-wrap: anywhere;
    line-height: 1.25;
    white-space: normal;
}

.reports-table >>> .reports-author-col .employee-link,
.reports-table >>> .reports-author-col .employee-link-wrap {
    min-width: 0;
    width: 100%;
}

.reports-table >>> .reports-author-col .employee-link__name {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
}

.reports-shift-date {
    color: #212529;
    font-size: 13px;
    line-height: 1.25;
}

.reports-count {
    align-items: center;
    display: flex;
    gap: 5px;
}

.reports-workers-count {
    justify-content: center;
}

.reports-media-trigger {
    border-radius: 4px;
    cursor: default;
    display: inline-flex;
    padding: 1px 3px;
}

.reports-media-trigger:focus {
    box-shadow: 0 0 0 3px rgba(13, 110, 253, .18);
    outline: 0;
}

.reports-media-statuses,
.reports-mobile-card__media-statuses {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 3px;
}

.reports-mobile-card__media-statuses {
    margin-bottom: 6px;
}

.reports-media-actions,
.reports-mobile-card__media-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
}

.reports-mobile-card__media-actions {
    margin-bottom: 8px;
}

.reports-media-action-button {
    align-items: center;
    display: inline-flex;
    font-size: 11px;
    font-weight: 700;
    gap: 4px;
    justify-content: center;
    line-height: 1.15;
    min-height: 27px;
    padding: 3px 7px;
    text-align: center;
    white-space: normal;
}

.reports-media-status {
    border: 1px solid #d7dde5;
    border-radius: 999px;
    color: #495057;
    display: inline-flex;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.15;
    padding: 2px 6px;
    white-space: normal;
}

.reports-media-status--success {
    background: #e8f5ec;
    border-color: #b7dfc0;
    color: #1e7e34;
}

.reports-media-status--warning {
    background: #fff7db;
    border-color: #f0d57a;
    color: #7a5600;
}

.reports-media-status--info {
    background: #e4f6f9;
    border-color: #a8dde6;
    color: #0c5460;
}

.reports-media-status--danger {
    background: #fde8eb;
    border-color: #f4b9c0;
    color: #b21f2d;
}

.reports-media-status--muted {
    background: #f5f7fa;
    color: #6c757d;
}

.reports-table >>> .reports-actions-cell {
    cursor: default;
    text-align: center;
}

.reports-row-actions {
    align-items: center;
    display: inline-flex;
    gap: 6px;
    justify-content: flex-end;
}

.reports-details-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-weight: 700;
    min-width: 86px;
}

@media (min-width: 768px) and (max-width: 1500px) {
    .reports-list {
        padding-right: 6px;
        padding-left: 6px;
    }

    .reports-details-button {
        width: 30px;
        min-width: 30px;
        height: 30px;
        padding: 0;
    }

    .reports-details-button span {
        display: none;
    }

    .reports-row-actions {
        gap: 4px;
    }
}

.reports-pagination {
    display: flex;
    flex: 0 0 auto;
    justify-content: center;
    padding: 6px 12px;
}

.reports-pagination >>> .pagination {
    margin-bottom: 0;
}

.reports-mobile-list {
    display: grid;
    gap: 10px;
    padding-bottom: 8px;
}

.reports-mobile-card {
    background: #fff;
    border: 1px solid #d7dde5;
    border-radius: 6px;
    color: #212529;
    cursor: pointer;
    display: block;
    padding: 10px 11px;
    text-align: left;
    width: 100%;
}

.reports-mobile-card:focus {
    box-shadow: 0 0 0 3px rgba(0, 123, 255, .18);
    outline: 0;
}

.reports-mobile-card__top,
.reports-mobile-card__meta {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: space-between;
}

.reports-mobile-card__top {
    margin-bottom: 6px;
}

.reports-mobile-card__id {
    align-items: center;
    display: inline-flex;
    gap: 5px;
}

.reports-mobile-card__status-actions {
    align-items: center;
    display: inline-flex;
    gap: 6px;
    margin-left: auto;
}

.reports-mobile-card__object {
    color: #212529;
    display: block;
    font-weight: 600;
    line-height: 1.25;
    margin: 4px 0 7px;
    overflow-wrap: anywhere;
}

a.reports-mobile-card__object:hover {
    color: #0056b3;
}

.reports-mobile-card__dates {
    color: #6c757d;
    display: grid;
    font-size: 12px;
    gap: 2px;
    margin-bottom: 7px;
}

.reports-mobile-card__dates span {
    align-items: center;
    display: inline-flex;
    gap: 5px;
}

.reports-mobile-card__submitted--late {
    color: #dc3545;
    font-weight: 700;
}

.reports-mobile-card__meta {
    color: #6c757d;
    font-size: 12px;
    gap: 6px;
    justify-content: flex-start;
    margin-bottom: 6px;
}

.reports-mobile-card__meta span {
    align-items: center;
    background: #f5f7fa;
    border: 1px solid #e3e8ef;
    border-radius: 999px;
    display: inline-flex;
    gap: 4px;
    line-height: 1.2;
    min-height: 24px;
    min-width: 0;
    overflow-wrap: anywhere;
    padding: 3px 7px;
}

.reports-mobile-card__meta span i {
    flex: 0 0 auto;
}

.reports-mobile-card__actions {
    display: flex;
    margin-top: 8px;
}

.reports-mobile-card__details {
    font-weight: 700;
    width: 100%;
}

.reports-mobile-card__media-actions .reports-media-action-button {
    flex: 1 1 150px;
}

.reports-mobile-card--delete-mode {
    padding-left: 10px;
}

.reports-mobile-card--selected {
    background: #fff5f5;
    border-color: #dc3545;
    box-shadow: inset 4px 0 0 #dc3545;
}

.reports-mobile-card--annulled {
    background: #fff3f3;
    border-color: #dc3545;
    box-shadow: inset 4px 0 0 #dc3545;
}

.reports-object-access-list {
    max-height: 56vh;
    overflow: auto;
}

.reports-object-access-button {
    white-space: normal;
}

.reports-object-access-name {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

@media (max-width: 767.98px) {
    .reports-page {
        height: auto;
        min-height: calc(100vh - 48px);
    }

    .reports-toolbar {
        padding: 8px 8px 4px;
    }

    .reports-toolbar__top {
        align-items: flex-start;
        flex-direction: column;
        gap: 6px;
    }

    .reports-toolbar__meta {
        width: 100%;
    }

    .reports-submit-button {
        flex: 1 1 100%;
    }

    .reports-access-button {
        margin-left: auto;
    }

    .reports-delete-bar {
        align-items: stretch;
        flex-direction: column;
        gap: 8px;
    }

    .reports-delete-bar__info {
        align-items: flex-start;
        flex-direction: column;
        gap: 3px;
    }

    .reports-delete-bar__actions {
        width: 100%;
    }

    .reports-delete-bar__actions .btn {
        flex: 1 1 0;
    }

    .reports-filters-shell {
        display: none;
    }

    .reports-filters-shell--open {
        display: block;
    }

    .reports-filter-select >>> .vs__dropdown-toggle,
    .reports-filters .form-control,
    .reports-filters .btn {
        min-height: 36px;
    }

    .reports-active-filters {
        flex-wrap: nowrap;
        margin-left: -2px;
        margin-right: -2px;
        overflow-x: auto;
        padding: 6px;
        scrollbar-width: thin;
    }

    .reports-active-filters__label,
    .reports-active-filters__reset,
    .reports-active-filter {
        flex: 0 0 auto;
    }

    .reports-list {
        padding: 0 8px;
        overflow: visible;
    }

    .reports-pagination {
        background: rgba(244, 246, 248, .96);
        bottom: 0;
        padding: 6px 8px 8px;
        position: sticky;
        z-index: 5;
    }
}
</style>

<style>
@keyframes reports-cell-highlight {
    0%,
    100% {
        background-color: transparent;
        box-shadow: none;
        filter: none;
    }
    50% {
        background-color: rgba(13, 110, 253, .2);
        box-shadow:
            inset 0 0 0 2px rgba(13, 110, 253, .9),
            0 0 16px rgba(13, 110, 253, .28);
        filter: brightness(.98);
    }
}

@keyframes reports-card-highlight {
    0%,
    100% {
        box-shadow: none;
        filter: none;
        outline-color: transparent;
    }
    50% {
        box-shadow:
            0 0 0 3px rgba(13, 110, 253, .95),
            0 0 18px rgba(13, 110, 253, .35);
        filter: brightness(.98);
        outline-color: rgba(13, 110, 253, .9);
    }
}

.reports-table tbody tr.reports-row--highlight > td {
    animation: reports-cell-highlight .34s ease-in-out 5;
    position: relative;
    z-index: 2;
}

.reports-mobile-card--highlight {
    animation: reports-card-highlight .34s ease-in-out 5;
    outline: 3px solid transparent;
    position: relative;
    z-index: 2;
}

.reports-media-popover {
    max-width: 260px;
}

.reports-media-popover .popover-body {
    padding: 8px;
}

.reports-media-preview-loading {
    align-items: center;
    color: #6c757d;
    display: flex;
    gap: 7px;
    min-width: 160px;
}

.reports-media-preview-grid {
    display: grid;
    gap: 6px;
    grid-template-columns: repeat(4, 48px);
}

.reports-media-preview-tile {
    align-items: center;
    aspect-ratio: 1 / 1;
    background: #eef1f5;
    border: 0;
    border-radius: 5px;
    color: #6c757d;
    display: flex;
    justify-content: center;
    overflow: hidden;
    padding: 0;
}

.reports-media-preview-tile img {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
}

.reports-media-preview-more {
    color: #6c757d;
    grid-column: 1 / -1;
}
</style>
